import {
    Box, Typography, Button, Paper, Alert, CircularProgress, Chip, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, Collapse
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import themeColors from "../theme";
import { useAuth } from "../context/AuthContext";
import {
    fetchPromoCodes, createPromoCode, updatePromoCode, deletePromoCode
} from "../api/promoCodesApi";

const EMPTY_FORM = { code: "", description: "", months: "12", maxRedemptions: "", expiresAt: "" };

/**
 * Admin management for trial-extension promo codes (e.g. the residency-graduate
 * code granting 12 months free).
 *
 * Codes are entered in months because that's how the offer is described to
 * users; the API stores days, which is what Stripe trials use.
 */
export default function PromoCodesSection() {
    const { teal, darkTeal } = themeColors;
    const { token } = useAuth();

    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            setCodes(await fetchPromoCodes(token));
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to load promo codes.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const setField = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

    const handleCreate = async () => {
        setSaving(true);
        setError(null);
        setMessage(null);
        try {
            const months = Number(form.months);
            if (!months || months < 1) throw new Error("Enter a free period of at least 1 month.");

            await createPromoCode(token, {
                code: form.code.trim().toUpperCase(),
                description: form.description.trim(),
                // 365/12 keeps "12 months" landing on exactly one year
                trialDays: Math.round(months * 365 / 12),
                maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
                expiresAt: form.expiresAt || null,
            });

            setMessage(`Code ${form.code.trim().toUpperCase()} created.`);
            setForm(EMPTY_FORM);
            setShowForm(false);
            load();
        } catch (err) {
            setError(err.response?.data?.error || err.message || "Failed to create code.");
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (promo) => {
        setError(null);
        try {
            await updatePromoCode(token, promo.id, { active: !promo.active });
            load();
        } catch (err) {
            setError(err.response?.data?.error || "Failed to update code.");
        }
    };

    const handleDelete = async (promo) => {
        const warning = promo.timesRedeemed > 0
            ? `${promo.code} has ${promo.timesRedeemed} redemption(s), so it will be deactivated rather than deleted. Continue?`
            : `Delete ${promo.code}? This cannot be undone.`;
        if (!window.confirm(warning)) return;

        setError(null);
        try {
            const result = await deletePromoCode(token, promo.id);
            setMessage(result.deactivated ? `${promo.code} deactivated.` : `${promo.code} deleted.`);
            load();
        } catch (err) {
            setError(err.response?.data?.error || "Failed to delete code.");
        }
    };

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6" color={teal} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalOfferIcon /> Promo codes
                </Typography>
                <Box>
                    <Tooltip title="Refresh">
                        <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
                    </Tooltip>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setShowForm((s) => !s)}
                        sx={{ textTransform: "none", fontWeight: 600, color: teal }}
                    >
                        New code
                    </Button>
                </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" mb={2}>
                Codes grant an extended free trial in place of the standard 3 months — they don't stack with it. A
                12-month code means 12 months free, then the normal $99/year.
            </Typography>

            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Collapse in={showForm}>
                <Box sx={{ p: 2, mb: 2, backgroundColor: "#F8F9FA", borderRadius: 1 }}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                        <TextField
                            size="small" label="Code" value={form.code} onChange={setField("code")}
                            placeholder="RESIDENCYGRAD" sx={{ minWidth: 180 }}
                            inputProps={{ style: { textTransform: "uppercase" } }}
                        />
                        <TextField
                            size="small" label="Free months" type="number" value={form.months}
                            onChange={setField("months")} sx={{ width: 130 }}
                            inputProps={{ min: 1, max: 24 }}
                        />
                        <TextField
                            size="small" label="Max redemptions" type="number" value={form.maxRedemptions}
                            onChange={setField("maxRedemptions")} sx={{ width: 160 }}
                            placeholder="Unlimited" inputProps={{ min: 1 }}
                        />
                        <TextField
                            size="small" label="Expires" type="date" value={form.expiresAt}
                            onChange={setField("expiresAt")} sx={{ width: 180 }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                    <TextField
                        fullWidth size="small" label="Description" value={form.description}
                        onChange={setField("description")} placeholder="2026 residency graduates" sx={{ mb: 2 }}
                    />
                    <Button
                        variant="contained" onClick={handleCreate} disabled={saving || !form.code.trim()}
                        sx={{ backgroundColor: teal, textTransform: "none", "&:hover": { backgroundColor: darkTeal } }}
                    >
                        {saving ? "Creating…" : "Create code"}
                    </Button>
                </Box>
            </Collapse>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} sx={{ color: teal }} />
                </Box>
            ) : codes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No promo codes yet.
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Code</strong></TableCell>
                                <TableCell><strong>Free period</strong></TableCell>
                                <TableCell><strong>Redeemed</strong></TableCell>
                                <TableCell><strong>Expires</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {codes.map((promo) => (
                                <TableRow key={promo.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{promo.code}</Typography>
                                        {promo.description && (
                                            <Typography variant="caption" color="text.secondary">
                                                {promo.description}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{promo.trialMonths} mo</TableCell>
                                    <TableCell>
                                        {promo.timesRedeemed}
                                        {promo.maxRedemptions ? ` / ${promo.maxRedemptions}` : ""}
                                    </TableCell>
                                    <TableCell>
                                        {promo.expiresAt ? new Date(promo.expiresAt).toLocaleDateString() : "—"}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={promo.active ? "Active" : "Inactive"}
                                            color={promo.active ? "success" : "default"}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={promo.active ? "Deactivate" : "Activate"}>
                                            <IconButton size="small" onClick={() => handleToggleActive(promo)}>
                                                {promo.active
                                                    ? <ToggleOnIcon fontSize="small" color="success" />
                                                    : <ToggleOffIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" onClick={() => handleDelete(promo)}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
}
